import { use, useCallback, useEffect, useRef, useState } from 'react'
import { getSelectedText } from '../api/word/word-service'
import { subscribeToSelectionChanges } from '../api/word/office-service'
import { MouseSelectionContext } from '../contexts/MouseSelectionContext'

/** Guard against hung Office/Word.run callbacks leaving the in-flight lock stuck. */
export const SELECTION_READ_TIMEOUT_MS = 5000

// Word may not have committed the new selection when blur/focus fires (Word Online).
const FOCUS_RECHECK_DELAY_MS = 150

// Office.js can silently drop callbacks when the task pane loses focus, a dialog is open,
// or the host is otherwise busy. Without a timeout the isCheckRunningRef guard stays true
// forever and all future selection events are silently dropped.
async function readSelectedTextWithTimeout(): Promise<string | null> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined

  try {
    return await Promise.race([
      getSelectedText(),
      new Promise<null>((resolve) => {
        timeoutId = setTimeout(() => resolve(null), SELECTION_READ_TIMEOUT_MS)
      }),
    ])
  } finally {
    if (timeoutId !== undefined) {
      clearTimeout(timeoutId)
    }
  }
}

/**
 * Owns selection tracking state and side effects. Used once at app root via
 * MouseSelectionProvider so the Office subscription survives route changes.
 */
export function useMouseSelectionTracker() {
  const [hasTextSelected, setHasTextSelected] = useState(false)

  // These two flags make sure we never run two API calls at the same time.
  // If a new selection event arrives while a call is already running, we remember
  // it and run one more check once the current call finishes.
  const isCheckRunningRef = useRef(false)
  const anotherCheckWaitingRef = useRef(false)
  const focusRecheckTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Asks Word for the selected text and updates state with the real answer.
  const checkIfTextIsSelected = useCallback(async () => {
    if (isCheckRunningRef.current) {
      anotherCheckWaitingRef.current = true
      return
    }

    isCheckRunningRef.current = true
    try {
      const selectedText = await readSelectedTextWithTimeout()
      setHasTextSelected(selectedText !== null)
    } catch {
      // If the API call fails for any reason, assume nothing is selected.
      setHasTextSelected(false)
    } finally {
      isCheckRunningRef.current = false
      // If a new event arrived while we were busy, run one more check now.
      if (anotherCheckWaitingRef.current) {
        anotherCheckWaitingRef.current = false
        void checkIfTextIsSelected()
      }
    }
  }, [])

  // Word calls this every time the selection changes (including simple cursor moves).
  const onDocumentSelectionChange = useCallback(() => {
    void checkIfTextIsSelected()
  }, [checkIfTextIsSelected])

  const scheduleFocusRecheck = useCallback(() => {
    if (focusRecheckTimerRef.current) {
      clearTimeout(focusRecheckTimerRef.current)
    }
    // DocumentSelectionChanged is unreliable in Word Online when focus moves between
    // the task pane and the document - re-read after the host settles.
    focusRecheckTimerRef.current = setTimeout(() => {
      void checkIfTextIsSelected()
    }, FOCUS_RECHECK_DELAY_MS)
  }, [checkIfTextIsSelected])

  useEffect(() => {
    // Check once on mount in case the user already has text selected.
    void checkIfTextIsSelected()

    const unsubscribe = subscribeToSelectionChanges(onDocumentSelectionChange)

    window.addEventListener('blur', scheduleFocusRecheck)
    window.addEventListener('focus', scheduleFocusRecheck)
    document.addEventListener('visibilitychange', scheduleFocusRecheck)

    return () => {
      unsubscribe()
      if (focusRecheckTimerRef.current) {
        clearTimeout(focusRecheckTimerRef.current)
      }
      window.removeEventListener('blur', scheduleFocusRecheck)
      window.removeEventListener('focus', scheduleFocusRecheck)
      document.removeEventListener('visibilitychange', scheduleFocusRecheck)
    }
  }, [checkIfTextIsSelected, onDocumentSelectionChange, scheduleFocusRecheck])

  return { hasMouseSelection: hasTextSelected }
}

/**
 * Returns whether the user currently has text selected in the Word document.
 * Must be used within MouseSelectionProvider (mounted at AppShell).
 */
export function useMouseSelection() {
  const context = use(MouseSelectionContext)
  if (context === undefined) {
    throw new Error('useMouseSelection must be used within MouseSelectionProvider')
  }
  return context
}
